import { IconButton, OdinIcon, OdinIconSize } from '@myosh/odin-components';
import { skipToken } from '@reduxjs/toolkit/query';
import { clsx } from 'clsx';
import { isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import type { RecordsAnalysisResultsDto } from '../../redux/config/widgetApi';
import { useGetRecordsInsightsResultsQuery } from '../../redux/config/widgetApi';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import type { SubmittedJobDetails } from '../../redux/slices/running-jobs';
import {
  JobStatus,
  removeJob,
  selectPanelOpened,
  selectSubmittedJobs,
  setJobStatus,
  setPanelOpened,
} from '../../redux/slices/running-jobs';
import { setWidgetAiInsightPanelOpened } from '../../redux/slices/widgetChart/WidgetChartSlice';

interface RunningJobsItemProps {
  job: SubmittedJobDetails;
  setIsPanelVisible: (visible: boolean) => void;
}

const RunningJobsItem = ({ job, setIsPanelVisible }: RunningJobsItemProps) => {
  const dispatch = useAppDispatch();
  const [jobId, setJobId] = useState<string | undefined>(job.id ?? undefined);
  const [jobData, setJobData] = useState<RecordsAnalysisResultsDto | undefined>();

  const { data: { result, status, error } = {}, isError } = useGetRecordsInsightsResultsQuery(
    jobId ? { analysisId: jobId } : skipToken,
    {
      pollingInterval: 5000,
    }
  );

  useEffect(() => {
    if (jobId) {
      if ('FINISHED' === status && result) {
        if (result) {
          toast.success('Record insight job completed');

          dispatch(
            setJobStatus({
              id: jobId,
              status: JobStatus.Success,
              widgetId: job.widgetId,
              completedDate: new Date().toISOString(),
              startedDate: job.startedDate,
            })
          );

          setJobData(result);
          setJobId(undefined);
        }
      } else if ('FAILED' === status || isError) {
        toast.error(error?.message || 'Record insight job failed');

        dispatch(
          setJobStatus({
            id: jobId,
            widgetId: job.widgetId,
            status: JobStatus.Failed,
            completedDate: new Date().toISOString(),
            startedDate: job.startedDate,
          })
        );

        setJobId(undefined);
      }
    }
  }, [status, isError, jobId, dispatch, error?.message, job.startedDate, job.widgetId, result]);

  useEffect(() => {
    setJobId(job.id);
  }, [job.id]);

  const itemStyles = clsx('flex gap-2 items-center min-w-0 w-full', {
    'hover:cursor-pointer': job.status === JobStatus.Success,
  });

  const removeIconStyles = clsx('w-4 h-4 rounded-full hover:bg-primary-5 hover:cursor-pointer', {
    'pointer-events-none': job.status === JobStatus.InProgress,
  });

  const onJobClick = (job: SubmittedJobDetails) => {
    if (job && jobData && job.name && job.status === JobStatus.Success) {
      setIsPanelVisible(false);
      dispatch(
        setWidgetAiInsightPanelOpened({
          show: true,
          title: job.name,
          isRecordInsight: true,
          value: jobData,
        })
      );
    }
  };

  const onJobRemove = (job: SubmittedJobDetails) => {
    if (job) {
      dispatch(removeJob(job));
    }
  };

  const getStatusIcon = (status?: JobStatus) => {
    if (status) {
      if (status === JobStatus.InProgress) {
        return <OdinIcon icon="Loader" size={OdinIconSize.ExtraSmall} spin />;
      } else if (status === JobStatus.Success) {
        return <OdinIcon icon="CircleCheck" size={OdinIconSize.ExtraSmall} className="text-success" />;
      } else if (status === JobStatus.Failed) {
        return <OdinIcon icon="CircleAlert" size={OdinIconSize.ExtraSmall} className="text-error" />;
      }
    }
  };

  return (
    <div key={job.id} className="flex items-center p-1 [&:has(div:hover)]:bg-primary-5">
      <div className={itemStyles} onClick={() => onJobClick(job)}>
        <OdinIcon icon="FileText" />
        <span className="flex-1 truncate" title={job.name}>
          {job.name}
        </span>
        <span>{getStatusIcon(job.status)}</span>
      </div>
      <span className={removeIconStyles} onClick={() => onJobRemove(job)}>
        {job.status !== JobStatus.InProgress && <OdinIcon icon="X" size={OdinIconSize.ExtraSmall} />}
      </span>
    </div>
  );
};

const RunningJobsPanel = () => {
  const panelButtonRef = useRef<HTMLButtonElement>(null);
  const dispatch = useAppDispatch();
  const submittedJobs = useAppSelector(selectSubmittedJobs);
  const panelOpened = useAppSelector(selectPanelOpened);

  const [isPanelVisible, setIsPanelVisible] = useState(panelOpened);
  const [hasViewedJobs, setHasViewedJobs] = useState(false);

  const onPanelButtonClick = () => {
    setIsPanelVisible(!isPanelVisible);
    if (!isPanelVisible) {
      dispatch(setPanelOpened(false));
    }
  };

  const onCloseButtonClick = () => {
    dispatch(setPanelOpened(false));
    setIsPanelVisible(false);
  };

  const getPanelButtonStatusIcon = () => {
    if (submittedJobs.length === 0) {
      return null;
    }
    const statusIconClasses = 'absolute top-[-10px] right-[-10px]';
    const latestJobStatus = submittedJobs[0].status;

    if (submittedJobs.some((job: SubmittedJobDetails) => job.status === JobStatus.InProgress)) {
      return <OdinIcon icon="Loader" size={OdinIconSize.ExtraSmall} className={statusIconClasses} spin />;
    } else if (hasViewedJobs) {
      return null;
    } else if (latestJobStatus === JobStatus.Failed) {
      return (
        <OdinIcon icon="CircleAlert" size={OdinIconSize.ExtraSmall} className={`${statusIconClasses} text-error`} />
      );
    } else if (latestJobStatus === JobStatus.Success) {
      return (
        <OdinIcon icon="CircleCheck" size={OdinIconSize.ExtraSmall} className={`${statusIconClasses} text-success`} />
      );
    } else {
      return null;
    }
  };

  useEffect(() => {
    if (isPanelVisible) {
      if (submittedJobs.every((job: SubmittedJobDetails) => job.status !== JobStatus.InProgress)) {
        setHasViewedJobs(true);
      } else {
        setHasViewedJobs(false);
      }
    }

    if (submittedJobs.length === 0) {
      setIsPanelVisible(false);
    }
  }, [isPanelVisible, submittedJobs]);

  useEffect(() => {
    setIsPanelVisible(panelOpened);
  }, [panelOpened]);

  return (
    <div className={clsx('ml-2 flex items-end', { hidden: submittedJobs.length === 0 })}>
      <div className="relative rounded-b-lg bg-mono-1">
        <button ref={panelButtonRef} onClick={onPanelButtonClick} className="relative">
          <OdinIcon icon="FolderDown" />
          {getPanelButtonStatusIcon()}
        </button>
        <div
          className={clsx('z-1 absolute top-10 min-w-[14rem] max-w-[25rem] rounded-lg bg-gray-5 shadow-lg', {
            hidden: !isPanelVisible,
          })}
        >
          <div className="flex items-center justify-between p-4">
            <span className="whitespace-nowrap">Job Status</span>
            <IconButton onClick={onCloseButtonClick} icon="X" iconSize={OdinIconSize.ExtraSmall} />
          </div>
          <div className="space-y-2 rounded-b-lg bg-mono-1 p-4">
            {!isEmpty(submittedJobs) &&
              submittedJobs.map((job: SubmittedJobDetails) => (
                <RunningJobsItem
                  key={`${job.widgetId}-${job.startedDate}`}
                  job={job}
                  setIsPanelVisible={setIsPanelVisible}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RunningJobsPanel;
